import { hasAuthErrorWithStatus, StatusCodes, usePasswordReset } from "@qw/auth";
import { Link, useParams } from "react-router";
import { z } from "zod";
import { Button } from "../button";
import { CardContent, CardFooter } from "../card";
import { FieldInput, Form, FormSubmit } from "../form";
import { Group } from "../group";
import { AuthCard } from "./auth-card";

interface LoginFormData {
  password: string;
  confirmPassword: string;
}

const validationSchema = z
  .object({
    password: z.string().min(8, { message: "Password must be at least 8 characters" }),
    confirmPassword: z.string().min(1, { message: "Please confirm your password" }),
  })
  .superRefine(({ confirmPassword, password }, ctx) => {
    if (!password) {
      return;
    }

    if (confirmPassword !== password) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Passwords don't match",
        path: ["confirmPassword"],
      });
    }
  });

const defaultValues = {
  password: "",
  confirmPassword: "",
};

export const AuthActivateAccount = () => {
  const { changePasswordId } = useParams();

  const [passwordReset, { data, error }] = usePasswordReset();

  if (!changePasswordId) {
    return null;
  }

  const handleSubmit = async ({ password }: LoginFormData) =>
    await passwordReset({ variables: { newPassword: password, changePasswordId } });

  const requestLinkExpired = hasAuthErrorWithStatus(error, StatusCodes.NotFound);

  // we may need to request a new link if the existing changePasswordId has expired
  if (requestLinkExpired) {
    // prompt to a password
    return (
      <AuthCard header="Create new password">
        <CardContent>
          The link is no longer valid. Your account may already be set up. If you forgot your password, you can{" "}
          <Button variant="link" theme="primary" asChild className="[font-size:inherit]">
            <Link to="/forgot-password">reset it</Link>
          </Button>
          .
        </CardContent>
      </AuthCard>
    );
  }

  // successfully reset password
  if (data?.passwordReset) {
    return (
      <AuthCard header="Create new password">
        <CardContent>
          Password successfully reset! Please{" "}
          <Button variant="link" theme="primary" asChild className="[font-size:inherit]">
            <Link to="/login">sign in</Link>
          </Button>
          .
        </CardContent>
      </AuthCard>
    );
  }

  // prompt to reset password
  return (
    <Form validationSchema={validationSchema} defaultValues={defaultValues} onSubmit={handleSubmit}>
      <AuthCard header="Create new password" subHeader="Please enter your new password below." error={error}>
        <CardContent>
          <Group className="gap-2">
            <FieldInput type="password" name="password" placeholder="Password" />
            <FieldInput type="password" name="confirmPassword" placeholder="Confirm Password" />
          </Group>
        </CardContent>
        <CardFooter>
          <FormSubmit className="flex-1">Set password</FormSubmit>
        </CardFooter>
      </AuthCard>
    </Form>
  );
};
